/* eslint-disable */
// CSV helpers (unchanged)
export const toCsvRows = (records) => {
  const rows = Array.isArray(records) ? records : [records];
  if (!rows.length) return { headers: [], lines: [] };
  const headers = Array.from(rows.reduce((set, r) => {
    Object.keys(r || {}).forEach(k => set.add(k)); return set;
  }, new Set()));
  const escape = (val) => `"${String(val ?? '').replace(/"/g, '""')}"`;
  const lines = rows.map(r => headers.map(h => escape(r[h])).join(','));
  return { headers, lines };
};

export const downloadFile = (filename, blob) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Export dealer list metrics to CSV using GetInventoriesWithMetrics (full table)
export async function exportDealerListCsv(getSelectedLocation, filePrefix = 'OEMDashboardDealerList_All') {
  const loc = typeof getSelectedLocation === 'function' ? getSelectedLocation() : null;
  if (!loc) { alert("Please select filters and click Get Report before exporting."); return; }
  const payload = { makePK: loc.makePK, locationType: loc.locationType, locationPK: loc.locationPK };

  try {
    const resp = await fetch('api/OEMDashboard/GetInventoriesWithMetrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) throw new Error(`GetInventoriesWithMetrics HTTP ${resp.status}`);
    const json = await resp.json();
    const list = Array.isArray(json?.inventoriesWithMetrics) ? json.inventoriesWithMetrics : [];

    // Map all visible table columns
    const rows = list.map(x => ({
      DealerName: x.dealerName,
      DealerCode: x.dealerCode,
      NetUtilizationPercent: x.netUtilization,
      SuggestedInventoryPctOfOnHandValue: x.suggestedInventoryPercentTotalOnHandValue,
      PercentageInventoryForStock: x.percentInventoryForStock,
      DaysSupply: x.daysSupply,
      OffTheShelfFillRatePercentage: x.offTheShelfFillRate,
      PercentageOnHandValueOverTwelveMonths: x.percentOnHandOver12Months,
      PartsEyePoints: x.partsEyePoints,
      SkippedOrderCount: x.skippedOrderCount,
      SkippedOrderValue: x.skippedOrderValue,
      ReturnLinesCount: x.returnLinesCount,
      ReturnLinesValue: x.returnLinesValue,
      OverridePartsCount: x.overridePartsCount,
      OverridePartsValue: x.overridePartsValue,
      IdleInventoryPercent: x.idleInventoryPercent,
      IdleInventoryValue: x.idleInventoryValue,
      TotalSuggestedOrderValue: x.totalSuggestedOrderValue,
      TotalNonSuggestedOrderValue: x.totalNonSuggestedOrderValue,
      TotalOnHandInventoryValue: x.totalOnHandInventoryValue
    }));

    const { headers, lines } = toCsvRows(rows);
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const csv = [headers.join(','), ...lines].join('\r\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

    downloadFile(`${filePrefix}_${stamp}.csv`, blob);
  } catch (e) {
    console.error('Export failed', e);
    alert('Failed to export dealer list data. Please try again.');
  }
}

// Print entire dealer list by fetching GetInventoriesWithMetrics and rendering a printable HTML table
export async function printDealerListPdf(getSelectedLocation, pageText = {}, opts = {}) {
  const { orientation = 'portrait', fitToOnePage = true } = opts || {};
  const loc = typeof getSelectedLocation === 'function' ? getSelectedLocation() : null;
  if (!loc) { alert("Run the report before printing."); return; }
  const payload = { makePK: loc.makePK, locationType: loc.locationType, locationPK: loc.locationPK };

  try {
    const resp = await fetch('api/OEMDashboard/GetInventoriesWithMetrics', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!resp.ok) throw new Error(`GetInventoriesWithMetrics HTTP ${resp.status}`);
    const json = await resp.json();
    const list = Array.isArray(json?.inventoriesWithMetrics) ? json.inventoriesWithMetrics : [];

    // Headers mirror OEMDashboardDealerList.jsx order; totals are on the right
    const headers = [
      pageText.dealerNameColumnHeader || 'Dealer Name',
      pageText.dealerCodeColumnHeader || 'Dealer Code',
      pageText.netUtilizationColumnHeader || 'Net Utilization %',
      pageText.suggestedInventoryPercentTotalOnHandValueColumnHeader || 'Suggested Inv % of On Hand',
      pageText.percentInventoryForStockColumnHeader || '% Inventory for Stock',
      pageText.daysSupplyColumnHeader || 'Days Supply',
      pageText.offTheShelfFillRateColumnHeader || 'Off-the-Shelf Fill Rate %',
      pageText.percentOnHandOver12MonthsColumnHeader || '% On Hand Over 12 Months',
      pageText.partsEyePointsColumnHeader || 'PartsEye Points',
      pageText.skippedOrdersColumnHeader || 'Skipped Orders',
      pageText.skippedOrderValueColumnHeader || 'Skipped Order Value',
      pageText.returnLinesColumnHeader || 'Return Lines',
      pageText.returnLinesValueColumnHeader || 'Return Lines Value',
      pageText.overridePartsCountColumnHeader || 'Override Parts Count',
      pageText.overridePartsValueColumnHeader || 'Override Parts Value',
      pageText.idleInventoryPercentColumnHeader || 'Idle Inventory %',
      pageText.idleInventoryValueColumnHeader || 'Idle Inventory Value',
      // Rightmost totals
      pageText.totalSuggestedOrderValueColumnHeader || 'Total Suggested Order Value',
      pageText.totalNonSuggestedValueColumnHeader || 'Total Non-Suggested Order Value',
      pageText.totalOnHandInventoryValueColumnHeader || 'Total On-Hand Inventory Value'
    ];

    const tableRows = list.map(x => [
      safe(x.dealerName),
      safe(x.dealerCode),
      fmtPct(x.netUtilization),
      fmtPct(x.suggestedInventoryPercentTotalOnHandValue),
      fmtPct(x.percentInventoryForStock),
      safe(x.daysSupply),
      fmtPct(x.offTheShelfFillRate),
      fmtPct(x.percentOnHandOver12Months),
      safe(x.partsEyePoints),
      safe(x.skippedOrderCount),
      fmtMoney(x.skippedOrderValue),
      safe(x.returnLinesCount),
      fmtMoney(x.returnLinesValue),
      safe(x.overridePartsCount),
      fmtMoney(x.overridePartsValue),
      fmtPct(x.idleInventoryPercent),
      fmtMoney(x.idleInventoryValue),
      fmtMoney(x.totalSuggestedOrderValue),
      fmtMoney(x.totalNonSuggestedOrderValue),
      fmtMoney(x.totalOnHandInventoryValue)
    ]);

    const printWindow = window.open('', '');
    if (!printWindow) { alert('Popup blocked. Please allow popups to print.'); return; }

    const title = pageText?.DealerListTitle || 'OEM Dashboard Dealer List';
    const pageRule = orientation === 'landscape'
      ? '@page { size: A4 landscape; margin: 10mm; }'
      : '@page { size: A4; margin: 10mm; }';

    const styles = `
      ${pageRule}
      html, body { margin: 0; padding: 0; font-family: Arial, sans-serif; font-size: 11px; }
      h1 { font-size: 16px; margin: 0 0 8px 0; }
      table { width: 100%; border-collapse: collapse; }
      thead th { position: sticky; top: 0; background: #f5f5f5; }
      th, td { border: 1px solid #ddd; padding: 4px 6px; text-align: left; vertical-align: top; }
      .numeric { text-align: right; }
      * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      /* When scaling to one page, we wrap content in #scale-root and apply transform */
      #scale-root { transform-origin: top left; }
    `;

    const headerHtml = `
      <thead>
        <tr>${headers.map(h => `<th>${escapeHtml(h)}</th>`).join('')}</tr>
      </thead>
    `;
    const bodyHtml = `
      <tbody>
        ${tableRows.map(row => `
          <tr>${row.map((cell, idx) => `<td class="${isNumericIndex(idx) ? 'numeric' : ''}">${escapeHtml(cell)}</td>`).join('')}</tr>
        `).join('')}
      </tbody>
    `;

    // A4 size + margins for computing scale when fitToOnePage=true
    const PAGE_W_MM = 210, PAGE_H_MM = 297, MARGIN_MM = 10, PX_PER_IN = 96, MM_PER_IN = 25.4;
    const pageWpx = Math.round((PAGE_W_MM / MM_PER_IN) * PX_PER_IN);
    const pageHpx = Math.round((PAGE_H_MM / MM_PER_IN) * PX_PER_IN);
    const marginPx = Math.round((MARGIN_MM / MM_PER_IN) * PX_PER_IN);
    const isLandscape = orientation === 'landscape';
    const printableW = (isLandscape ? pageHpx : pageWpx) - marginPx * 2;
    const printableH = (isLandscape ? pageWpx : pageHpx) - marginPx * 2;

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>${escapeHtml(title)}</title>
          <style>${styles}</style>
        </head>
        <body>
          <div id="scale-root">
            <h1>${escapeHtml(title)}</h1>
            <table>
              ${headerHtml}
              ${bodyHtml}
            </table>
          </div>
          <script>
            (function(){
              var fit = ${fitToOnePage ? 'true' : 'false'};
              if (!fit) {
                setTimeout(function(){ window.print(); window.close(); }, 250);
                return;
              }
              // Measure and scale to fit printable area
              var root = document.getElementById('scale-root');
              var rect = root.getBoundingClientRect();
              var printableW = ${printableW};
              var printableH = ${printableH};
              var scaleX = printableW / rect.width;
              var scaleY = printableH / rect.height;
              var scale = Math.min(scaleX, scaleY);
              // Apply scale
              root.style.transform = 'scale(' + scale + ')';
              // Reserve the original space so printing engine doesn't clip
              root.style.width = rect.width + 'px';
              root.style.height = rect.height + 'px';
              setTimeout(function(){ window.print(); window.close(); }, 300);
            })();
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();

  } catch (err) {
    console.error('Print full dealer list failed', err);
    alert('Failed to generate dealer list print.');
  }
}

// Screenshot-style print: capture header + full scroll area at full width (no text scaling),
// slice into multiple A4 pages at 1:1 CSS pixels. Auto-switch to landscape if needed.
export async function printRenderedImage(selector = '.dealer-list-table-wrapper') {
  try {
    const ensureHtml2Canvas = async () => {
      if (window.html2canvas) return window.html2canvas;
      try {
        const mod = await import(/* webpackIgnore: true */'https://cdn.jsdelivr.net/npm/html2canvas@1.4.1/dist/html2canvas.min.js');
        return window.html2canvas || mod.default || mod;
      } catch {
        return window.html2canvas;
      }
    };

    const html2canvas = await ensureHtml2Canvas();
    if (!html2canvas) {
      alert("html2canvas is not available. Install via npm or include the CDN script.");
      return;
    }

    // Target wrapper (header + table). If your scroll container differs, adjust here.
    const wrapper = document.querySelector(selector);
    if (!wrapper) { alert('Nothing to print. Target selector not found.'); return; }

    // Bootstrap tables typically wrap in .table-responsive; capture that if present
    const scrollContainer = wrapper.querySelector('.table-responsive') || wrapper;
    const tableEl = scrollContainer.querySelector('table');

    // Preserve and relax constraints so all columns/rows render
    const prev = {
      bodyOverflow: document.body.style.overflow,
      wrapWidth: wrapper.style.width,
      wrapMinWidth: wrapper.style.minWidth,
      wrapMaxWidth: wrapper.style.maxWidth,
      wrapHeight: wrapper.style.height,
      wrapOverflow: wrapper.style.overflow,
      wrapMaxHeight: wrapper.style.maxHeight,
      scWidth: scrollContainer.style.width,
      scMinWidth: scrollContainer.style.minWidth,
      scMaxWidth: scrollContainer.style.maxWidth,
      scHeight: scrollContainer.style.height,
      scOverflow: scrollContainer.style.overflow,
      scMaxHeight: scrollContainer.style.maxHeight
    };

    document.body.style.overflow = 'visible';
    wrapper.style.overflow = 'visible';
    wrapper.style.maxHeight = 'none';

    scrollContainer.style.overflow = 'visible';
    scrollContainer.style.maxHeight = 'none';

    // Compute full size of the scrollable content (prefer the actual table width)
    const fullWidth = Math.max(
      tableEl ? tableEl.scrollWidth : 0,
      scrollContainer.scrollWidth,
      scrollContainer.clientWidth
    );
    const fullHeight = Math.max(scrollContainer.scrollHeight, scrollContainer.clientHeight);

    // Expand widths so html2canvas sees full horizontal content
    wrapper.style.width = `${fullWidth}px`;
    wrapper.style.minWidth = `${fullWidth}px`;
    wrapper.style.maxWidth = 'none';

    scrollContainer.style.width = `${fullWidth}px`;
    scrollContainer.style.minWidth = `${fullWidth}px`;
    scrollContainer.style.maxWidth = 'none';

    // Expand height to reveal all rows
    scrollContainer.style.height = `${fullHeight}px`;

    // Ensure we start from the leftmost content
    scrollContainer.scrollLeft = 0;

    // Capture at device pixel ratio (no artificial downscale)
    const CAPTURE_SCALE = Math.max(1, (window.devicePixelRatio || 1));
    const canvas = await html2canvas(scrollContainer, {
      scale: CAPTURE_SCALE,
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      width: fullWidth,
      height: fullHeight,
      windowWidth: Math.max(document.documentElement.clientWidth, fullWidth),
      windowHeight: Math.max(document.documentElement.clientHeight, fullHeight),
      scrollX: 0,
      scrollY: 0
    });

    // Restore styles
    scrollContainer.style.width = prev.scWidth;
    scrollContainer.style.minWidth = prev.scMinWidth;
    scrollContainer.style.maxWidth = prev.scMaxWidth;
    scrollContainer.style.height = prev.scHeight;
    scrollContainer.style.overflow = prev.scOverflow;
    scrollContainer.style.maxHeight = prev.scMaxHeight;

    wrapper.style.width = prev.wrapWidth;
    wrapper.style.minWidth = prev.wrapMinWidth;
    wrapper.style.maxWidth = prev.wrapMaxWidth;
    wrapper.style.height = prev.wrapHeight;
    wrapper.style.overflow = prev.wrapOverflow;
    wrapper.style.maxHeight = prev.wrapMaxHeight;

    document.body.style.overflow = prev.bodyOverflow;

    // Page sizing (A4 with margins). We will NOT scale the content to a single page.
    // Choose orientation to preserve full width at 1:1 CSS px when possible.
    const PX_PER_IN = 96;
    const MM_PER_IN = 25.4;

    const portrait = { wMM: 210, hMM: 297, marginMM: 12 };
    const landscape = { wMM: 297, hMM: 210, marginMM: 12 };

    const toPx = (mm) => Math.round((mm / MM_PER_IN) * PX_PER_IN);

    const cssWidthCaptured = Math.round(canvas.width / CAPTURE_SCALE);
    const cssHeightCaptured = Math.round(canvas.height / CAPTURE_SCALE);

    // Compute printable area for both orientations
    const portraitWpx = toPx(portrait.wMM), portraitHpx = toPx(portrait.hMM), portraitMargin = toPx(portrait.marginMM);
    const landscapeWpx = toPx(landscape.wMM), landscapeHpx = toPx(landscape.hMM), landscapeMargin = toPx(landscape.marginMM);

    const portraitPrintableW = portraitWpx - portraitMargin * 2;
    const portraitPrintableH = portraitHpx - portraitMargin * 2;
    const landscapePrintableW = landscapeWpx - landscapeMargin * 2;
    const landscapePrintableH = landscapeHpx - landscapeMargin * 2;

    // Pick orientation that can accommodate width at 1:1 CSS px
    const useLandscape = cssWidthCaptured > portraitPrintableW && cssWidthCaptured <= landscapePrintableW;
    const page = useLandscape ? landscape : portrait;
    const pageWpx = useLandscape ? landscapeWpx : portraitWpx;
    const pageHpx = useLandscape ? landscapeHpx : portraitHpx;
    const pageMarginPx = useLandscape ? landscapeMargin : portraitMargin;
    const printableW = useLandscape ? landscapePrintableW : portraitPrintableW;
    const printableH = useLandscape ? landscapePrintableH : portraitPrintableH;

    // Slice height in device pixels for 1:1 CSS px mapping
    const sliceHeightPx = printableH * CAPTURE_SCALE;
    const sliceWidthPx = canvas.width;

    const pageImages = [];
    for (let y = 0; y < canvas.height; y += sliceHeightPx) {
      const h = Math.min(sliceHeightPx, canvas.height - y);
      const slice = document.createElement('canvas');
      slice.width = sliceWidthPx;
      slice.height = h;
      const sctx = slice.getContext('2d', { alpha: false });
      sctx.drawImage(canvas, 0, y, sliceWidthPx, h, 0, 0, sliceWidthPx, h);
      pageImages.push({
        dataUrl: slice.toDataURL('image/png'),
        cssWidth: cssWidthCaptured,                 // use CSS px in print (no scaling)
        cssHeight: Math.round(h / CAPTURE_SCALE)
      });
    }

    const win = window.open('', '_blank');
    if (!win) { alert('Popup blocked. Please allow popups to print.'); return; }

    // Build print document: one image per page, no scaling beyond 1:1 CSS px.
    win.document.open();
    win.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>Dealer List Print</title>
          <style>
            @page { size: ${page.wMM}mm ${page.hMM}mm; margin: ${page.marginMM}mm; }
            html, body { margin: 0; padding: 0; }
            .page {
              width: ${page.wMM}mm;
              height: ${page.hMM}mm;
              box-sizing: border-box;
              display: flex;
              align-items: flex-start;
              justify-content: center;
              page-break-after: always;
            }
            .content {
              width: ${printableW}px;
              height: ${printableH}px;
              box-sizing: border-box;
              display: flex;
              align-items: flex-start;
              justify-content: flex-start; /* anchor left so wide images aren't centered & clipped both sides */
              overflow: hidden; /* clip right if still wider than printable area */
            }
            img {
              image-rendering: -webkit-optimize-contrast;
              image-rendering: crisp-edges;
            }
            * { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          </style>
        </head>
        <body>
          ${pageImages.map(img => `
            <div class="page">
              <div class="content">
                <img src="${img.dataUrl}" width="${img.cssWidth}" height="${img.cssHeight}" alt="Dealer List Page" />
              </div>
            </div>
          `).join('')}
          <script>
            window.addEventListener('load', function() {
              setTimeout(function(){ window.print(); window.close(); }, 200);
            });
          </script>
        </body>
      </html>
    `);
    win.document.close();

  } catch (err) {
    console.error('Print rendered image failed', err);
    alert('Failed to generate print image.');
  }
}

// Helpers
function safe(v) {
  if (v == null) return '';
  return String(v);
}
function fmtPct(v) {
  if (v == null || v === '') return '';
  return `${v}%`;
}
function fmtMoney(v) {
  if (v == null || v === '') return '';
  const n = Number(v);
  if (isNaN(n)) return String(v);
  return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}
function isNumericIndex(idx) {
  return idx >= 2;
}